import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../../api/client";
import { useWorkspaceConfig } from "../../../context/WorkspaceConfigContext";
import GenericTable from "../../../components/GenericTable";

function formatUpdatedAt(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

function TextValueEditor({ row, onSave, savingId }) {
  const [draft, setDraft] = useState(row.value ?? "");
  const dirty = String(draft) !== String(row.value ?? "");
  const saving = savingId === row.id;

  useEffect(() => {
    setDraft(row.value ?? "");
  }, [row.id, row.value]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", py: 0.5 }}>
      <TextField
        size="small"
        fullWidth
        multiline={String(draft).length > 80}
        maxRows={4}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && dirty && !saving) {
            e.preventDefault();
            onSave(row, draft);
          }
        }}
        disabled={saving}
        inputProps={{ "aria-label": row.name || row.key }}
      />
      <Tooltip title="Salvar">
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={!dirty || saving}
            onClick={() => onSave(row, draft)}
          >
            {saving ? <CircularProgress size={18} /> : <SaveOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

/**
 * Espelho do legado Administrar → Configurações.
 * Lista/edita a tabela `configuracoes` (somente master — rota + API).
 */
const PlatformConfigurations = () => {
  const { t } = useTranslation(["settings", "app"]);
  const { refetch: refetchWorkspaceConfig } = useWorkspaceConfig();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [savingId, setSavingId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchData("/configurations/admin", "GET");
      const items = res?.data?.items || res?.items || [];
      setRows(Array.isArray(items) ? items : []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("platformConfigurationsPage.loadError")
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const saveValue = useCallback(
    async (row, nextValue) => {
      const id = row?.id;
      if (!id) return;
      const value = nextValue == null ? "" : String(nextValue);
      try {
        setSavingId(id);
        await fetchData(`/configurations/admin/${id}`, "PUT", { value });
        setRows((prev) =>
          prev.map((r) =>
            r.id === id
              ? {
                  ...r,
                  value,
                  updatedAt: new Date().toISOString(),
                }
              : r
          )
        );
        setSnack({
          open: true,
          message: t("platformConfigurationsPage.saved"),
          severity: "success",
        });
        try {
          await refetchWorkspaceConfig?.();
        } catch {
          /* cache allowlist é best-effort */
        }
      } catch (err) {
        setSnack({
          open: true,
          message:
            err?.response?.data?.message ||
            err?.message ||
            t("platformConfigurationsPage.saveError"),
          severity: "error",
        });
      } finally {
        setSavingId(null);
      }
    },
    [refetchWorkspaceConfig, t]
  );

  const filteredRows = useMemo(() => {
    const q = String(search || "")
      .trim()
      .toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = `${r.name || ""} ${r.key || ""} ${r.value || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: t("platformConfigurationsPage.colConfig"),
        flex: 1.2,
        minWidth: 220,
        sortable: true,
        renderCell: (params) => {
          const row = params.row;
          const tip = t("platformConfigurationsPage.lastChanged", {
            user: row.updatedBy || "—",
            at: formatUpdatedAt(row.updatedAt),
          });
          return (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 1, width: "100%" }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {row.name || row.key}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {row.key}
                </Typography>
              </Box>
              <Tooltip title={tip}>
                <InfoOutlinedIcon fontSize="small" color="action" sx={{ mt: 0.25, flexShrink: 0 }} />
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "value",
        headerName: t("platformConfigurationsPage.colValue"),
        flex: 1.6,
        minWidth: 280,
        sortable: false,
        renderCell: (params) => {
          const row = params.row;
          const tipo = Number(row.type);

          if (tipo === 1) {
            const checked = String(row.value) === "1" || String(row.value).toLowerCase() === "true";
            return (
              <Switch
                checked={checked}
                disabled={savingId === row.id}
                onChange={(e) => saveValue(row, e.target.checked ? "1" : "0")}
                inputProps={{ "aria-label": row.name || row.key }}
              />
            );
          }

          return <TextValueEditor row={row} onSave={saveValue} savingId={savingId} />;
        },
      },
    ],
    [saveValue, savingId, t]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t("platformConfigurationsPage.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("platformConfigurationsPage.subtitle")}
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <GenericTable
        rows={filteredRows}
        columns={columns}
        rowCount={filteredRows.length}
        pageSize={pageSize}
        page={page}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        paginationMode="client"
        isLoading={loading}
        checkboxSelection={false}
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        getRowHeight={() => "auto"}
        tableMaxHeight={640}
        dataGridSx={{
          "& .MuiDataGrid-cell": {
            alignItems: "center",
            py: 0.5,
          },
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlatformConfigurations;
